import { useDipLabels } from "~/features/dataImportProjects/hooks/useDipLabels";
import type { DipSummaryGroup, DipSummaryTone } from "~/features/dataImportProjects/types";
import { cn } from "~/lib/utils";

interface DipOutputSummaryProps {
  groups: DipSummaryGroup[];
}

const SUMMARY_MARKER_CLASS: Record<DipSummaryTone, string> = {
  dataSource: "bg-score-b",
  evidenceDrafts: "bg-score-a",
  evidenceSet: "bg-score-a",
  changeRequests: "bg-primary-500",
  records: "bg-primary-500",
};

const SUMMARY_CARD_CLASS = "rounded-[var(--radius-md)] border border-border-default bg-bg-surface px-4 py-3.5";

function metricGroupIndex(metric: DipSummaryGroup["metrics"][number]): number {
  if (metric.tone === "evidenceSet" || metric.tone === "records") return 1;
  if (metric.tone === "dataSource" && metric.labelKey === "summary.ready") return 1;
  return 0;
}

function findMetric(group: DipSummaryGroup, labelKey: string) {
  return group.metrics.find((metric) => metric.labelKey === labelKey);
}

function sumMetricValues(metrics: Array<DipSummaryGroup["metrics"][number] | undefined>): number | null {
  if (metrics.some((metric) => metric == null || metric.value == null)) return null;
  return metrics.reduce((sum, metric) => sum + (metric?.value ?? 0), 0);
}

function SummaryMetric({
  metric,
  labels,
}: {
  metric?: DipSummaryGroup["metrics"][number];
  labels: Record<string, string>;
}) {
  if (!metric) return null;
  return (
    <span className="inline-flex min-w-0 items-baseline gap-1.5">
      <span className="text-[22px] font-bold leading-none text-fg-2">{metric.value == null ? "-" : metric.value}</span>
      <span className="text-12-bold text-fg-2">{labels[metric.labelKey] ?? metric.labelKey}</span>
    </span>
  );
}

function SummaryTotal({ value, label }: { value: number | null; label: string }) {
  return (
    <span className="inline-flex min-w-0 items-baseline gap-1.5">
      <span className="text-[22px] font-bold leading-none text-fg-2">{value == null ? "-" : value}</span>
      <span className="text-12-bold text-fg-2">{label}</span>
    </span>
  );
}

function SummaryHeader({ label, tone }: { label: string; tone: DipSummaryTone }) {
  return (
    <div className="flex items-center gap-2">
      <span className={cn("size-2.5 rounded-[3px]", SUMMARY_MARKER_CLASS[tone])} aria-hidden="true" />
      <strong className="block text-14-bold leading-5 text-fg-1">{label}</strong>
    </div>
  );
}

function RecordsSummaryCard({ group, labels }: { group: DipSummaryGroup; labels: Record<string, string> }) {
  const open = findMetric(group, "summary.changeRequestsOpen");
  const approved = findMetric(group, "summary.changeRequestsApproved");
  const rejected = findMetric(group, "summary.changeRequestsRejected");
  const entities = findMetric(group, "summary.entities");
  const relations = findMetric(group, "summary.relations");
  const reviewedCount = sumMetricValues([approved, rejected]);
  const recordsCount = sumMetricValues([entities, relations]);

  return (
    <div className={SUMMARY_CARD_CLASS}>
      <SummaryHeader label={labels[group.titleKey] ?? group.titleKey} tone="records" />
      <div className="mt-3 grid grid-cols-[minmax(0,1fr)_1px_minmax(0,1fr)_1px_minmax(0,1fr)] items-stretch gap-3">
        <div className="flex min-w-0 flex-col justify-start gap-1.5">
          <SummaryMetric metric={open} labels={labels} />
        </div>
        <div className="bg-border-default" aria-hidden="true" />
        <div className="flex min-w-0 flex-col gap-1.5">
          <SummaryTotal value={reviewedCount} label={labels["summary.changeRequestsReviewed"]} />
          <div className="flex min-w-0 flex-col gap-1">
            <SummaryMetric metric={approved} labels={labels} />
            <SummaryMetric metric={rejected} labels={labels} />
          </div>
        </div>
        <div className="bg-border-default" aria-hidden="true" />
        <div className="flex min-w-0 flex-col gap-1.5">
          <SummaryTotal value={recordsCount} label={labels["summary.records"]} />
          <div className="flex min-w-0 flex-col gap-1">
            <SummaryMetric metric={entities} labels={labels} />
            <SummaryMetric metric={relations} labels={labels} />
          </div>
        </div>
      </div>
    </div>
  );
}

export function DipOutputSummary({ groups }: DipOutputSummaryProps) {
  const labels = useDipLabels();
  return (
    <section>
      <h3 className="text-16-bold leading-5">{labels["summary.title"]}</h3>
      <div className="mt-4 grid gap-3 lg:grid-cols-[minmax(220px,0.9fr)_minmax(220px,0.9fr)_minmax(340px,1.2fr)]">
        {groups.map((group) => {
          if (group.titleKey === "summary.records") {
            return <RecordsSummaryCard key={group.titleKey} group={group} labels={labels} />;
          }

          const sectionTone = group.metrics[0]?.tone ?? "records";
          const leftMetrics = group.metrics.filter((metric) => metricGroupIndex(metric) === 0);
          const rightMetrics = group.metrics.filter((metric) => metricGroupIndex(metric) === 1);
          return (
            <div key={group.titleKey} className={SUMMARY_CARD_CLASS}>
              <SummaryHeader label={labels[group.titleKey] ?? group.titleKey} tone={sectionTone} />
              <div className="mt-3 grid grid-cols-[minmax(0,1fr)_1px_minmax(0,1fr)] items-stretch gap-3">
                <div className="flex min-w-0 flex-col gap-1.5">
                  {leftMetrics.map((metric) => (
                    <SummaryMetric key={metric.labelKey} metric={metric} labels={labels} />
                  ))}
                </div>
                <div className="bg-border-default" aria-hidden="true" />
                <div className="flex min-w-0 flex-col gap-1.5">
                  {rightMetrics.map((metric) => (
                    <SummaryMetric key={metric.labelKey} metric={metric} labels={labels} />
                  ))}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
